import { apiService } from "../api.service";

// Requests
import {
  BranchGeneralSettingsRequest,
  BranchServiceTypesRequest,
  WorkDaysRequest,
  ServiceMenuRequest,
  StaffMemberRequest
} from "../../models/api/requests";

// Domain Models
import {
  Branch,
  BranchServiceType,
  WorkDay,
  SpecialDay,
  ServiceMenu,
  Role,
  StaffMemeber,
  DashboardData
} from "../../models/app/domain";

/**
 * Fetch branch general settings Request
 * @param headerBranchId - id
 * @return - BranchResponse || ErrorResponse
 */
export const fetchBranchGeneralSettings = async headerBranchId => {
  const response = await apiService.get(
    "/branches/settings?with_images=true",
    headerBranchId
  );
  if (response.hasError) {
    return response;
  }
  return { branch: new Branch(response.data.branch) };
};

/**
 * Update branch general settings Request
 * @return - BranchResponse || ErrorResponse
 */
export const updateBranchGeneralSettingsRequest = async (
  branchGeneralSettingsPayload,
  initialValues,
  headerBranchId
) => {
  const payload = new BranchGeneralSettingsRequest(
    branchGeneralSettingsPayload
  );
  payload.removeSameValues(initialValues);
  const response = await apiService.patch(
    `/branches/settings`,
    payload,
    headerBranchId
  );
  if (response.hasError) {
    return response;
  }
  return { branch: new Branch(response.data.branch) };
};

/**
 * Branch Activation Request
 * @return - BranchResponse || ErrorResponse
 */
export const branchActivationRequest = async (
  branchActivationPayload,
  headerBranchId
) => {
  const payload = { is_active: branchActivationPayload.isActive };
  const response = await apiService.post(
    `/branches/activate`,
    payload,
    headerBranchId
  );
  if (response.hasError) {
    return response;
  }
  return { isActive: response.data.is_active };
};

/**
 * Fetch branch service types and capacity Request
 * @param headerBranchId - id
 * @return - BranchServiceTypesResponse || ErrorResponse
 */
export const fetchBranchServiceTypes = async headerBranchId => {
  const response = await apiService.get(
    "/branches/service-types",
    headerBranchId
  );
  if (response.hasError) {
    return response;
  }
  return {
    branchServiceTypes: response.data.branch_service_types.map(
      type => new BranchServiceType(type)
    )
  };
};

/**
 * Update branch service types and capacity Request
 * @param headerBranchId - id
 * @return - BranchServiceTypesResponse || ErrorResponse
 */
export const updateBranchServiceTypes = async (formValues, headerBranchId) => {
  const payload = new BranchServiceTypesRequest(formValues);
  const response = await apiService.post(
    "/branches/service-types",
    payload,
    headerBranchId
  );
  if (response.hasError) {
    return response;
  }
  return {
    branchServiceTypes: response.data.branch_service_types.map(
      type => new BranchServiceType(type)
    )
  };
};

/**
 * Fetch branch work days Request
 * @param headerBranchId - id
 * @return - WorkDaysResponse || ErrorResponse
 */
export const fetchWorkDays = async headerBranchId => {
  const response = await apiService.get(
    "/branches/schedule/work-days",
    headerBranchId
  );
  if (response.hasError) {
    return response;
  }
  return {
    workDays: response.data.work_days.map(day => new WorkDay(day))
  };
};

/**
 * Update branch work days Request
 * @param headerBranchId - id
 * @return - WorkDaysResponse || ErrorResponse
 */
export const updateWorkDays = async (formValues, headerBranchId) => {
  const payload = new WorkDaysRequest(formValues);
  const response = await apiService.post(
    "/branches/schedule/work-days",
    payload,
    headerBranchId
  );
  if (response.hasError) {
    return response;
  }
  return {
    workDays: response.data.work_days.map(day => new WorkDay(day))
  };
};

/**
 * Fetch branch special days Request
 * @param headerBranchId - id
 * @return - SpecialDaysResponse || ErrorResponse
 */
export const fetchSpecialDays = async headerBranchId => {
  const response = await apiService.get(
    "/branches/schedule/special-days",
    headerBranchId
  );
  if (response.hasError) {
    return response;
  }
  return {
    specialDays: response.data.special_days.map(day => new SpecialDay(day))
  };
};

/**
 * Create branch special day Request
 * @param headerBranchId - id
 * @return - SpecialDayResponse || ErrorResponse
 */
export const createSpecialDayRequest = async (
  specialDayFormValues,
  headerBranchId
) => {
  const payload = { name: specialDayFormValues.name, date: specialDayFormValues.date };
  const response = await apiService.post(
    "/branches/schedule/special-days",
    payload,
    headerBranchId
  );
  if (response.hasError) {
    return response;
  }
  return { specialDay: new SpecialDay(response.data.special_day) };
};

/**
 * Delete branch special day Request
 * @param headerBranchId - id
 * @return - MessageResponse || ErrorResponse
 */
export const deleteSpecialDayRequest = async (specialDayId, headerBranchId) => {
  const response = await apiService.delete(
    `/branches/schedule/special-days/${specialDayId}`,
    headerBranchId
  );
  if (response.hasError) {
    return response;
  }
  return { message: response.data.message };
};

/**
 * Fetch service-menu Request
 * @param headerBranchId - id
 * @param serviceType - string
 * @param search - string
 * @return - ServiceMenuResponse || ErrorResponse
 */
export const fetchServiceMenuRequest = async (headerBranchId, serviceType, search) => {
  const params = new URLSearchParams();
  if (serviceType) params.append("service_type", serviceType);
  if (search) params.append("search", search);
  const query = params.toString() ? `?${params.toString()}` : "";
  const response = await apiService.get(
    `/branches/service-menu${query}`,
    headerBranchId
  );
  if (response.hasError) {
    return response;
  }
  return {
    serviceMenu: response.data.service_menu.map(menu => new ServiceMenu(menu))
  };
};

/**
 * Fetch onboarding status Request
 * Aggregated, authoritative status that backs the dashboard setup checklist
 * (verify/plan/branch/service types/schedule/go-live + staff/roles counts).
 * @param headerBranchId - active branch id (optional; falls back to x-branch)
 * @return - OnboardingStatusResponse || ErrorResponse
 */
export const fetchOnboardingStatusRequest = async headerBranchId => {
  const response = await apiService.get(
    "/branches/onboarding-status",
    headerBranchId
  );
  if (response.hasError) {
    return response;
  }
  const status = response.data?.onboarding_status || {};
  return {
    // Business-level (also feeds the verify/plan chip via Redux dashboardData).
    dashboardData: new DashboardData(status.dashboard_data),

    // Branch existence + active-branch signals.
    hasBranch: status.has_branch === true,
    isActive: status.is_active === true,
    hasServiceTypes: status.has_service_types === true,
    hasWorkSchedule: status.has_work_schedule === true,
    hasEquipment: status.has_equipment === true,

    // Counts (0 when no branch / nothing set up yet).
    serviceMenuCount: status.service_menu_count || 0,
    staffCount: status.staff_count || 0,
    rolesCount: status.roles_count || 0
  };
};

/**
 * Exports the full service menu of the branch as a PDF blob. The
 * endpoint deliberately ignores the search / type filters that the
 * listing endpoint accepts — the export is always the complete menu
 * grouped by service type.
 *
 * @param headerBranchId - Branch id (sent via x-branch header).
 * @return - { data: Blob } || ErrorResponse
 */
export const exportServiceMenuPDF = async (headerBranchId) => {
  return apiService.getBlob(`/branches/service-menu/export`, headerBranchId);
};

/**
 * Create service  Request
 * @param headerBranchId - id
 * @param serviceMenuFormValues - string
 * @return - ServiceMenuResponse || ErrorResponse
 */
export const createServiceMenuRequest = async (
  headerBranchId,
  serviceMenuFormValues
) => {
  const payload = new ServiceMenuRequest(serviceMenuFormValues);
  const response = await apiService.post(
    `/branches/service-menu`,
    payload,
    headerBranchId
  );
  if (response.hasError) {
    return response;
  }
  return {
    serviceMenu: response.data.service_menu.map(menu => new ServiceMenu(menu))
  };
};

/**
 * Fetch service-menu Request
 * @param headerBranchId - id
 * @param serviceMenuFormValues - string
 * @return - ServiceMenuResponse || ErrorResponse
 */
export const updateServiceMenuRequest = async (
  headerBranchId,
  serviceMenuFormValues,
  serviceMenuInitial,
  serviceId
) => {
  const payload = new ServiceMenuRequest(serviceMenuFormValues);
  payload.removeSameValues(serviceMenuInitial);
  const response = await apiService.patch(
    `/branches/service-menu/${serviceId}`,
    payload,
    headerBranchId
  );
  if (response.hasError) {
    return response;
  }
  return {
    serviceMenu: response.data.service_menu.map(menu => new ServiceMenu(menu))
  };
};

/**
 * Delete service-menu Request
 * @param serviceId - id
 * @return - ServiceMenuResponse || ErrorResponse
 */
export const deleteServiceMenuRequest = async (headerBranchId, serviceId) => {
  const response = await apiService.delete(
    `/branches/service-menu/${serviceId}`,
    headerBranchId
  );
  if (response.hasError) {
    return response;
  }
  return {
    serviceMenu: response.data.service_menu.map(menu => new ServiceMenu(menu))
  };
};

/**
 * Fetch roles Request
 * @param headerBranchId - id
 * @return - RolesResponse || ErrorResponse
 */
export const fetchRolesRequest = async headerBranchId => {
  const response = await apiService.get(`/roles`, headerBranchId);
  if (response.hasError) {
    return response;
  }
  return {
    roles: response.data.roles.map(role => new Role(role))
  };
};

/**
 * Create role Request
 * @param headerBranchId - id
 * @return - RolesResponse || ErrorResponse
 */
export const createRoleRequest = async (headerBranchId, roleFormValues) => {
  const payload = {
    name: roleFormValues.name,
    description: roleFormValues.description,
    permissions: roleFormValues.permissions
  };
  const response = await apiService.post(`/roles`, payload, headerBranchId);
  if (response.hasError) {
    return response;
  }
  return {
    roles: response.data.roles.map(role => new Role(role))
  };
};

/**
 * Update role Request
 * @param headerBranchId - id
 * @return - RolesResponse || ErrorResponse
 */
export const updateRoleRequest = async (
  headerBranchId,
  roleFormValues,
  roleId
) => {
  const payload = {
    name: roleFormValues.name,
    description: roleFormValues.description,
    permissions: roleFormValues.permissions
  };
  const response = await apiService.put(
    `/roles/${roleId}`,
    payload,
    headerBranchId
  );
  if (response.hasError) {
    return response;
  }
  return {
    roles: response.data.roles.map(role => new Role(role))
  };
};

/**
 * Delete role Request
 * @param roleId - id
 * @return - RolesResponse || ErrorResponse
 */
export const deleteRoleRequest = async (headerBranchId, roleId) => {
  const response = await apiService.delete(`/roles/${roleId}`, headerBranchId);
  if (response.hasError) {
    return response;
  }
  return {
    roles: response.data.roles.map(role => new Role(role))
  };
};

/**
 * Fetch staff memebers Request
 * @param headerBranchId - id
 * @return - StaffMemebersResponse || ErrorResponse
 */
export const fetchStaffMembersRequest = async headerBranchId => {
  const response = await apiService.get(`branches/staff`, headerBranchId);
  if (response.hasError) {
    return response;
  }
  return {
    staffMembers: response.data.staff_members.map(
      member => new StaffMemeber(member)
    )
  };
};

/**
 * Create staff member Request
 * @param headerBranchId - id
 * @return - StaffMemebersResponse || ErrorResponse
 */
export const createStaffMemberRequest = async (
  headerBranchId,
  staffMemberValues
) => {
  const payload = new StaffMemberRequest(staffMemberValues);
  const response = await apiService.post(
    `branches/staff`,
    payload,
    headerBranchId
  );
  if (response.hasError) {
    return response;
  }
  return {
    staffMembers: response.data.staff_members.map(
      member => new StaffMemeber(member)
    )
  };
};

/**
 * Update staff member Request
 * @param headerBranchId - id
 * @param serviceMenuFormValues - string
 * @return - ServiceMenuResponse || ErrorResponse
 */
export const updateStaffMemberRequest = async (
  headerBranchId,
  staffMemberValues,
  initialValues,
  staffMemberId
) => {
  const payload = new StaffMemberRequest(staffMemberValues);
  payload.removeSameValues(initialValues);
  const response = await apiService.patch(
    `branches/staff/${staffMemberId}`,
    payload,
    headerBranchId
  );
  if (response.hasError) {
    return response;
  }
  return {
    staffMembers: response.data.staff_members.map(
      member => new StaffMemeber(member)
    )
  };
};

/**
 * Delete staff member Request
 * @param staffMemberId - id
 * @return - StaffMemebersResponse || ErrorResponse
 */
export const deleteStaffMemberRequest = async (
  headerBranchId,
  staffMemberId
) => {
  const response = await apiService.delete(
    `branches/staff/${staffMemberId}`,
    headerBranchId
  );
  if (response.hasError) {
    return response;
  }
  return {
    staffMembers: response.data.staff_members.map(
      member => new StaffMemeber(member)
    )
  };
};
